import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, Typography, QRCode, Radio, Space } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Title, Text, Paragraph } = Typography;

interface LoginProps {
  onLogin: () => void;
}

interface Challenge {
  challenge: string;
  session: string;
  username: string;
  mfaOptions?: string[];
}

const MFA_LABELS: Record<string, string> = {
  SOFTWARE_TOKEN_MFA: 'Authenticator app',
  SMS_MFA: 'Text message (SMS)',
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<Challenge | null>(null);
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [loginName, setLoginName] = useState('');

  const finish = (data: any) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify({ username: loginName }));
    onLogin();
  };

  // Every auth call returns either tokens or the next challenge.
  const handleResult = async (data: any) => {
    if (data.access_token) return finish(data);
    setStep(data);
    setSetupSecret(null);
    if (data.challenge === 'MFA_SETUP') {
      const res = await axios.post(`${API_URL}/auth/mfa/setup`, {
        session: data.session,
        username: data.username,
      });
      setSetupSecret(res.data.secretCode);
      setStep({ ...data, session: res.data.session });
    }
  };

  const call = async (path: string, body: object) => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/${path}`, body);
      await handleResult(res.data);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      // Expired/invalid session can't be retried, so restart from credentials
      if (/session expired/i.test(message)) setStep(null);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(null);
    setSetupSecret(null);
    setError('');
  };

  const renderStep = () => {
    if (!step) {
      return (
        <Form name="login" onFinish={(v) => { setLoginName(v.username); call('login', v); }} autoComplete="off" layout="vertical">
          <Form.Item name="username" rules={[{ required: true, message: 'Please input your username!' }]}>
            <Input prefix={<UserOutlined />} placeholder="Username" size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Please input your password!' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
          </Form.Item>
          <SubmitButton loading={loading} label="Login" />
        </Form>
      );
    }

    const base = { session: step.session, username: step.username };

    switch (step.challenge) {
      case 'NEW_PASSWORD_REQUIRED':
        return (
          <Form name="new-password" onFinish={(v) => call('new-password', { ...base, newPassword: v.newPassword })} layout="vertical">
            <Paragraph type="secondary">Please set a new password to continue.</Paragraph>
            <Form.Item name="newPassword" rules={[{ required: true, message: 'Please enter a new password' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="New password" size="large" />
            </Form.Item>
            <Form.Item
              name="confirm"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm the password' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue('newPassword') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error('Passwords do not match')),
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Confirm password" size="large" />
            </Form.Item>
            <SubmitButton loading={loading} label="Set password" />
          </Form>
        );

      case 'MFA_SETUP':
        return (
          <Form name="mfa-setup" onFinish={(v) => call('mfa/setup/verify', { ...base, code: v.code })} layout="vertical">
            <Paragraph type="secondary">
              Two-factor authentication is required. Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code.
            </Paragraph>
            {setupSecret ? (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <QRCode
                  value={`otpauth://totp/PMS%20Extractor:${encodeURIComponent(step.username)}?secret=${setupSecret}&issuer=PMS%20Extractor`}
                />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" copyable={{ text: setupSecret }}>Can't scan? Copy the setup key</Text>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: 16 }}><Text type="secondary">Loading…</Text></div>
            )}
            <CodeInput />
            <SubmitButton loading={loading} label="Verify and sign in" disabled={!setupSecret} />
          </Form>
        );

      case 'SELECT_MFA_TYPE':
        return (
          <Form
            name="mfa-select"
            initialValues={{ mfaType: step.mfaOptions?.[0] }}
            onFinish={(v) => call('mfa/select', { ...base, mfaType: v.mfaType })}
            layout="vertical"
          >
            <Paragraph type="secondary">Choose how to receive your verification code.</Paragraph>
            <Form.Item name="mfaType">
              <Radio.Group>
                <Space direction="vertical">
                  {(step.mfaOptions || []).map(o => (
                    <Radio key={o} value={o}>{MFA_LABELS[o] || o}</Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>
            <SubmitButton loading={loading} label="Continue" />
          </Form>
        );

      case 'SOFTWARE_TOKEN_MFA':
      case 'SMS_MFA':
        return (
          <Form
            name="mfa-verify"
            onFinish={(v) => call('mfa/verify', { ...base, challenge: step.challenge, code: v.code })}
            layout="vertical"
          >
            <Paragraph type="secondary">
              {step.challenge === 'SMS_MFA'
                ? 'Enter the code we texted to your phone.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </Paragraph>
            <CodeInput />
            <SubmitButton loading={loading} label="Verify" />
          </Form>
        );

      default:
        return <Alert type="error" showIcon message={`Unsupported sign-in step: ${step.challenge}`} />;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            {step ? 'Verify it’s you' : 'Admin Login'}
          </Title>
          <Text type="secondary">PMS Data Extractor</Text>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

        {renderStep()}

        {step && (
          <Button type="link" block onClick={reset}>Back to login</Button>
        )}
      </Card>
    </div>
  );
};

const CodeInput: React.FC = () => (
  <Form.Item
    name="code"
    rules={[{ required: true, message: 'Please enter the code' }]}
  >
    <Input
      prefix={<SafetyOutlined />}
      placeholder="Verification code"
      size="large"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={8}
      autoFocus
    />
  </Form.Item>
);

const SubmitButton: React.FC<{ loading: boolean; label: string; disabled?: boolean }> = ({ loading, label, disabled }) => (
  <Form.Item>
    <Button type="primary" htmlType="submit" loading={loading} disabled={disabled} block size="large">
      {label}
    </Button>
  </Form.Item>
);

export default Login;
